import {useEffect, useRef} from "react";


export function useKey(
    key: string,
    callback: (e: KeyboardEvent) => void,
    options?: {
        priority?: number;
        suppressBelow?: boolean;
        enabled?: boolean;
    }
) {
    const enabled = options?.enabled ?? true;
    const idRef = useRef<string | null>(null);

    useEffect(() => {
        if (!enabled) {
            // unregister if previously registered
            if (idRef.current) {
                keyboardRegistry.unregister(key, idRef.current);
                idRef.current = null;
            }
            return;
        }

        const id = crypto.randomUUID();
        idRef.current = id;

        keyboardRegistry.register(key, {
            id,
            priority: options?.priority ?? 0,
            suppressBelow: options?.suppressBelow ?? false,
            callback,
        });

        return () => {
            keyboardRegistry.unregister(key, id);
            idRef.current = null;
        };
    }, [
        key,
        callback,
        enabled,
        options?.priority,
        options?.suppressBelow,
    ]);
}

export function isTextInput(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;

    return (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
    );
}

class KeyboardRegistry {
    private handlers = new Map<string, KeyHandler[]>();

    register(key: string, handler: KeyHandler) {
        console.log('registering handler for ', key)
        const list = this.handlers.get(key) ?? [];
        this.handlers.set(key, [...list, handler]);
    }

    unregister(key: string, id: string) {
        const list = this.handlers.get(key);
        if (!list) return;
        this.handlers.set(
            key,
            list.filter(h => h.id !== id)
        );
    }

    handle(e: KeyboardEvent) {
        const list = this.handlers.get(e.code);
        if (!list) return;

        // Highest priority first
        const sorted = [...list].sort(
            (a, b) => b.priority - a.priority
        );

        for (const h of sorted) {
            h.callback(e);

            if (h.suppressBelow) {
                e.preventDefault();
                e.stopPropagation();
                break;
            }

            if (e.defaultPrevented) break;
        }
    }
}

export const keyboardRegistry = new KeyboardRegistry();

type KeyHandler = {
    id: string;
    priority: number;
    suppressBelow: boolean;
    callback: (e: KeyboardEvent) => void;
};
